import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { FollowersFollowingModal } from '@components/modals/UserStats/FollowersFollowing';
import { ModalContext, ModalType } from '@context/modal';
import {
  type FragmentType,
  getFragment,
  RecommendedFollowsUserCellFragmentDoc,
} from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RecommendedFollowsUserCell on User {
    id
    webappUri
    publicAddress
    username
    avatar {
      id
      url
    }
    artist {
      id
    }
    followerCount
    ...FollowersFollowingModal
  }
`);

const IMAGE_SIZE = 40;

export function RecommendedFollowsUserCell({
  userFrag,
}: {
  userFrag: FragmentType<RecommendedFollowsUserCellFragmentDoc>;
}) {
  const user = getFragment(RecommendedFollowsUserCellFragmentDoc, userFrag);
  const { openModal } = ModalContext.useContainer();
  const {
    id: userId,
    webappUri,
    publicAddress: walletAddress,
    username,
    avatar,
    followerCount,
    artist,
  } = user;

  const userURI = webappUriPath(webappUri);
  const isArtist = !!artist?.id;
  return (
    <div className="flex flex-col items-center gap-2 lt:flex-row">
      <div className="aspect-square h-10 w-10 overflow-hidden rounded-full bg-neutral100">
        <Avatar
          webappUri={userURI}
          username={username}
          walletAddress={walletAddress}
          src={avatar?.url}
          size={IMAGE_SIZE}
          borderRadius={999}
        />
      </div>
      <div className="flex flex-1 flex-col gap-0.5">
        <AccessibleLink pathnameUri={userURI} className="flex items-center">
          <div className="font-title text-title-xxs font-medium text-base600">{username}</div>
          {isArtist && (
            <>
              {SPACE}
              <VerifiedTooltip size={16} />
            </>
          )}
        </AccessibleLink>
        <button
          aria-label="Follower Count - Click to view followers"
          className="w-fit appearance-none text-base-xs font-medium text-base500 transition-all duration-500 ease-in-out hover:text-base600"
          onClick={() => {
            openModal(ModalType.FOLLOWERS_FOLLOWING, {
              body: <FollowersFollowingModal followData={user} />,
            });
          }}
        >
          {followerCount}
          {SPACE}
          {getSingularOrPlural({
            word: 'follower',
            count: followerCount,
          })}
        </button>
      </div>
      <FollowButton
        size="XS"
        sourceLocation="Activity Feed Recommended Following"
        userId={userId}
        artistId={artist?.id}
      />
    </div>
  );
}
